import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Tree,
  Table,
  Input,
  Button,
  Select,
  Modal,
  Space,
  message,
  Tag,
  Image,
  Card,
  Row,
  Col,
  Dropdown,
  Menu,
  Popconfirm,
  Tabs,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  FileTextOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  AppstoreOutlined,
  EllipsisOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import MaterialGroupModal from '../components/MaterialGroupModal';
import MaterialSelector from '../components/MaterialSelector';
import MaterialPreview from '../components/MaterialPreview';
import api from '../services/api';

const { Sider, Content } = Layout;
const { Search } = Input;
const { Option } = Select;



const Materials = () => {
  const navigate = useNavigate();

  // 状态管理
  const [groupTree, setGroupTree] = useState([]);
  const [groupsMap, setGroupsMap] = useState(new Map()); // 保存原始分组数据的映射
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  // 从localStorage读取上次选择的分组，默认为-1（全部）
  const getLastSelectedGroupId = () => {
    try {
      const lastGroupId = localStorage.getItem('materials_last_selected_group');
      return lastGroupId ? parseInt(lastGroupId) : -1;
    } catch (error) {
      return -1;
    }
  };

  // 保存选择的分组到localStorage并更新状态
  const saveAndSetSelectedGroupId = (groupId) => {
    setSelectedGroupId(groupId);
    try {
      localStorage.setItem('materials_last_selected_group', groupId?.toString() || '-1');
    } catch (error) {
      console.warn('保存最后选择的分组失败:', error);
    }
  };

  const [selectedGroupId, setSelectedGroupId] = useState(getLastSelectedGroupId()); // -1表示选中"全部"
  const [selectedMaterialType, setSelectedMaterialType] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // 网格视图使用较大的分页大小
  const getPageSize = () => {
    return viewMode === 'grid' ? 24 : 20;
  };

  // 模态框状态
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [materialModalVisible, setMaterialModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialType, setMaterialType] = useState('TEXT');

  // 选中的素材
  const [selectedMaterials, setSelectedMaterials] = useState([]);

  // 移动素材模态框
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [movingMaterial, setMovingMaterial] = useState(null);

  // 批量移动素材模态框
  const [batchMoveModalVisible, setBatchMoveModalVisible] = useState(false);
  const [batchMoveSelectedKeys, setBatchMoveSelectedKeys] = useState([]);
  const [batchMoveTreeKey, setBatchMoveTreeKey] = useState(0);

  // 悬停状态管理
  const [hoveredGroupId, setHoveredGroupId] = useState(null);

  // 视图模式状态 - 支持列表和网格两种模式
  const [viewMode, setViewMode] = useState('grid'); // 'list' 或 'grid'

  // 获取租户ID
  const getTenantId = () => {
    try {
      const tenantStr = localStorage.getItem('tenant');
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        return tenant.id;
      }
    } catch (error) {
      console.error('获取租户ID失败:', error);
    }
    return null;
  };

  const tenantId = getTenantId();

  // 素材类型配置
  const materialTypes = [
    { value: null, label: '全部类型', icon: <AppstoreOutlined /> },
    { value: 'TEXT', label: '文本', icon: <FileTextOutlined /> },
    { value: 'IMAGE', label: '图片', icon: <FileImageOutlined /> },
    { value: 'VIDEO', label: '视频', icon: <VideoCameraOutlined /> },
    { value: 'MINIPROGRAM', label: '小程序', icon: <AppstoreOutlined /> },
    { value: 'LINK', label: '网页', icon: <LinkOutlined /> }
  ];

  // 获取OSS预览URL
  const getOssPreviewUrl = async (ossUrl) => {
    if (!ossUrl) return null;

    // 如果已经是HTTP URL，直接返回
    if (ossUrl.startsWith('http')) {
      return ossUrl;
    }

    // 如果是OSS协议URL，请求转换
    if (ossUrl.startsWith('oss://')) {
      try {
        const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(ossUrl)}`);
        const data = await response.json();

        if (data.success && data.url) {
          return data.url;
        }
      } catch (error) {
        console.error('获取预览URL失败:', error);
      }
    }

    return ossUrl; // 后备方案，返回原URL
  };

  // 批量处理素材预览URL
  const processMaterialPreviewUrls = async (materialsData) => {
    const processedMaterials = [];

    for (const material of materialsData) {
      const processedMaterial = { ...material };

      try {
        // 处理图片素材
        if (material.materialType === 'IMAGE' && material.imageInfo?.url) {
          const previewUrl = await getOssPreviewUrl(material.imageInfo.url);
          processedMaterial.imageInfo = {
            ...material.imageInfo,
            previewUrl: previewUrl
          };
        }

        // 处理视频素材
        if (material.materialType === 'VIDEO' && material.videoInfo?.url) {
          const previewUrl = await getOssPreviewUrl(material.videoInfo.url);
          processedMaterial.videoInfo = {
            ...material.videoInfo,
            previewUrl: previewUrl
          };

          // 对于视频，尝试生成缩略图URL（如果有的话）
          if (material.videoInfo.thumbnailUrl) {
            const thumbnailPreviewUrl = await getOssPreviewUrl(material.videoInfo.thumbnailUrl);
            processedMaterial.videoInfo.thumbnailPreviewUrl = thumbnailPreviewUrl;
          }
        }

        // 处理链接素材的封面图
        if (material.materialType === 'LINK' && material.linkInfo?.picUrl) {
          const previewUrl = await getOssPreviewUrl(material.linkInfo.picUrl);
          processedMaterial.linkInfo = {
            ...material.linkInfo,
            picPreviewUrl: previewUrl
          };
        }

        // 处理小程序素材的封面图
        if (material.materialType === 'MINIPROGRAM' && material.miniprogramInfo?.picUrl) {
          const previewUrl = await getOssPreviewUrl(material.miniprogramInfo.picUrl);
          processedMaterial.miniprogramInfo = {
            ...material.miniprogramInfo,
            picPreviewUrl: previewUrl
          };
        }
      } catch (error) {
        console.error('处理素材预览URL失败:', error);
      }

      processedMaterials.push(processedMaterial);
    }

    return processedMaterials;
  };

  // 加载分组树
  const loadGroupTree = async () => {
    if (!tenantId) {
      message.error('未找到租户信息，请重新登录');
      return;
    }

    try {
      const response = await api.get('/material-groups/tree', {
        params: { tenantId }
      });
      if (response.data.success) {
        // 构建分组映射（只包含真实分组，过滤虚拟节点）
        const map = new Map();
        const buildMap = (groups) => {
          groups.forEach(group => {
            // 只添加真实分组到映射中，过滤掉虚拟节点（"全部"和"未分组"）
            if (group.id !== null && group.id !== -1 && typeof group.id === 'number') {
              map.set(group.id, group);
            }
            if (group.children && group.children.length > 0) {
              buildMap(group.children);
            }
          });
        };
        buildMap(response.data.data);
        setGroupsMap(map);

        setGroupTree(convertToTreeData(response.data.data));
      }
    } catch (error) {
      message.error('加载分组失败');
    }
  };

  // 转换分组数据为Tree组件格式（带管理操作）
  const convertToTreeData = (groups) => {
    return groups.map(group => ({
      key: group.id === null ? 'ungrouped_main' : group.id,
      title: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            minHeight: '24px',
            paddingRight: '4px'
          }}
          onMouseEnter={() => setHoveredGroupId(group.id)}
          onMouseLeave={() => setHoveredGroupId(null)}
        >
          <span style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {group.name}
          </span>
          {/* "全部"和"未分组"节点不显示操作按钮 */}
          {group.id !== null && group.id !== -1 && (
            <div className="materials-tree-actions" style={{ position: 'relative', zIndex: 999, flexShrink: 0 }}>
              <Dropdown
                overlay={() => getGroupActionMenu(group.id, group)}
                trigger={['click']}
                placement="bottomRight"
                getPopupContainer={() => document.body}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<EllipsisOutlined rotate={90} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  style={{
                    opacity: hoveredGroupId === group.id ? 1 : 0.4,
                    transition: 'opacity 0.2s',
                    minWidth: '24px',
                    height: '24px',
                    backgroundColor: hoveredGroupId === group.id ? '#f0f0f0' : 'transparent',
                    borderRadius: '4px',
                    pointerEvents: 'auto'
                  }}
                />
              </Dropdown>
            </div>
          )}
        </div>
      ),
      children: group.children ? convertToTreeData(group.children) : []
    }));
  };

  // 转换分组数据为纯净的Tree格式（用于选择，无操作按钮）
  const convertToSimpleTreeData = (groups) => {
    return groups.map(group => ({
      key: group.id,
      title: group.name,
      children: group.children ? convertToSimpleTreeData(group.children) : []
    }));
  };

  // 转换分组数据为纯净的Tree格式（不包含"未分组"节点，用于移动功能）
  const convertToPureTreeData = (groups) => {
    return groups.map(group => ({
      key: group.id,
      title: group.name,
      children: group.children ? convertToPureTreeData(group.children) : []
    }));
  };

  // 加载素材列表
  const loadMaterials = async (page = 1) => {
    if (!tenantId) {
      message.error('未找到租户信息，请重新登录');
      return;
    }

    setLoading(true);
    try {
      const params = {
        tenantId,
        materialType: selectedMaterialType,
        keyword: searchKeyword,
        page: page - 1,
        size: getPageSize()
      };

      // 处理分组ID
      console.log('当前selectedGroupId:', selectedGroupId, '类型:', typeof selectedGroupId);

      if (selectedGroupId === -1) {
        // 选中了"全部"，显示全部素材，不传递任何分组参数
        console.log('选中全部，不传递分组参数');
      } else if (selectedGroupId === null) {
        // 选中了"未分组"，只显示未分组的素材
        params.ungrouped = true;
        console.log('选中未分组，设置ungrouped=true');
      } else {
        // 选中了具体分组
        params.groupId = selectedGroupId;
        console.log('选中具体分组:', selectedGroupId);
      }

      console.log('最终请求参数:', params);
      const response = await api.get('/materials', { params });

      if (response.data.success) {
        // 处理预览URL
        const processedMaterials = await processMaterialPreviewUrls(response.data.data.content);
        setMaterials(processedMaterials);

        setPagination({
          current: page,
          pageSize: getPageSize(),
          total: response.data.data.totalElements
        });
      }
    } catch (error) {
      message.error('加载素材失败');
    } finally {
      setLoading(false);
    }
  };



  // 初始化加载
  useEffect(() => {
    loadGroupTree();
    loadMaterials();
  }, []);

  // 监听筛选条件变化
  useEffect(() => {
    loadMaterials(1);
  }, [selectedGroupId, selectedMaterialType, searchKeyword, viewMode]);

  // 分组选择
  const handleGroupSelect = (selectedKeys) => {
    const selectedKey = selectedKeys[0];
    console.log('Tree选中的key:', selectedKey, '类型:', typeof selectedKey);

    // 处理Tree组件的key转换：
    // -1: 全部
    // "0-1" 或类似: Tree组件对null的字符串化表示，转换回null表示未分组
    // 数字字符串: 具体分组ID，需要转换为数字

    let actualGroupId;
    if (selectedKey === -1 || selectedKey === '-1') {
      actualGroupId = -1; // 全部
    } else if (selectedKey === 'ungrouped_main' || selectedKey === null || selectedKey === 'null' || typeof selectedKey === 'string' && selectedKey.includes('-')) {
      actualGroupId = null; // 未分组（Tree组件可能把null转换成各种字符串形式）
    } else {
      // 具体分组ID
      actualGroupId = typeof selectedKey === 'string' ? parseInt(selectedKey) : selectedKey;
    }

    console.log('处理后的groupId:', actualGroupId);
    saveAndSetSelectedGroupId(actualGroupId);
  };

  // 搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
  };

  // 创建分组
  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupModalVisible(true);
  };

  // 编辑分组
  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupModalVisible(true);
  };

  // 创建子分组
  const handleCreateSubGroup = (parentGroup) => {
    setEditingGroup({ parentId: parentGroup.id });
    setGroupModalVisible(true);
  };

  // 删除分组
  const handleDeleteGroup = async (groupId) => {
    try {
      const response = await api.delete(`/material-groups/${groupId}`);
      if (response.data.success) {
        message.success('删除成功');
        loadGroupTree();
        // 如果删除的是当前选中的分组，重置为"全部"
        if (selectedGroupId === groupId) {
          saveAndSetSelectedGroupId(-1);
        }
      } else {
        // 显示后端返回的具体错误信息
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      // 处理网络错误或其他异常
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('删除失败：' + error.message);
      }
    }
  };

  // 分组操作菜单
  const getGroupActionMenu = (groupId, groupFromTree = null) => {
    // 优先使用传入的group对象，否则从groupsMap获取
    const group = groupFromTree || groupsMap.get(groupId);

    // 如果还是找不到分组数据，返回基本菜单
    if (!group) {
      console.warn('无法获取分组数据，groupId:', groupId);
      return (
        <Menu>
          <Menu.Item key="loading" disabled>
            数据加载中...
          </Menu.Item>
        </Menu>
      );
    }

    return (
      <Menu>
        <Menu.Item
          key="addSub"
          onClick={() => handleCreateSubGroup(group)}
        >
          添加子分组
        </Menu.Item>
        <Menu.Item
          key="edit"
          onClick={() => handleEditGroup(group)}
        >
          编辑
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          key="delete"
          danger
          onClick={() => {
            Modal.confirm({
              title: '确认删除',
              content: `确定要删除分组"${group.name}"吗？分组下有素材时无法删除。`,
              onOk: () => handleDeleteGroup(groupId)
            });
          }}
        >
          删除
        </Menu.Item>
      </Menu>
    );
  };

  // 创建素材
  const handleCreateMaterial = (type) => {
    let url = `/materials/create?type=${type}`;

    // 如果已选中某个具体分组（不是"全部"和"未分组"），将分组ID带到创建页面
    if (selectedGroupId !== -1 && selectedGroupId !== null && selectedGroupId !== undefined) {
      url += `&groupId=${selectedGroupId}`;
    }

    navigate(url);
  };

  // 编辑素材
  const handleEditMaterial = (material) => {
    navigate(`/materials/edit/${material.id}?type=${material.materialType}`);
  };

  // 删除素材
  const handleDeleteMaterial = async (materialId) => {
    try {
      const response = await api.delete(`/materials/${materialId}`);
      if (response.data.success) {
        message.success('删除成功');
        loadMaterials(pagination.current);
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedMaterials.length === 0) {
      message.warning('请选择要删除的素材');
      return;
    }

    try {
      const response = await api.delete('/materials/batch', {
        data: { ids: selectedMaterials }
      });
      if (response.data.success) {
        message.success('批量删除成功');
        setSelectedMaterials([]);
        loadMaterials(pagination.current);
      }
    } catch (error) {
      message.error('批量删除失败');
    }
  };

  // 批量移动素材
  const handleBatchMove = () => {
    if (selectedMaterials.length === 0) {
      message.warning('请选择要移动的素材');
      return;
    }
    setBatchMoveSelectedKeys([]); // 重置选中状态
    setBatchMoveTreeKey(Date.now()); // 强制Tree组件重新渲染
    setBatchMoveModalVisible(true);
  };

  // 确认批量移动素材
  const handleConfirmBatchMove = async (targetGroupId) => {
    if (selectedMaterials.length === 0) {
      message.warning('请选择要移动的素材');
      return;
    }

    console.log('批量移动素材到分组:', targetGroupId, '素材IDs:', selectedMaterials);

    try {
      const response = await api.put('/materials/batch/move', {
        ids: selectedMaterials,
        groupId: targetGroupId
      });

      if (response.data.success) {
        message.success(`成功移动 ${selectedMaterials.length} 个素材`);
        setBatchMoveModalVisible(false);
        setBatchMoveSelectedKeys([]); // 重置选中状态
        setSelectedMaterials([]);
        loadMaterials(pagination.current);
      } else {
        message.error(response.data.message || '批量移动失败');
      }
    } catch (error) {
      console.error('批量移动失败:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('批量移动失败：' + error.message);
      }
    }
  };

  // 复制素材
  const handleCopyMaterial = async (material) => {
    const copyData = {
      ...material,
      name: `${material.name} (副本)`,
      id: undefined
    };

    try {
      const response = await api.post(`/materials/${material.id}/copy`, copyData);
      if (response.data.success) {
        message.success('复制成功');

        // 获取新创建的素材ID
        const newMaterialId = response.data.data?.id;
        if (newMaterialId) {
          // 跳转到编辑页面
          navigate(`/materials/edit/${newMaterialId}?type=${material.materialType}`);
        } else {
          // 如果没有返回新ID，则刷新列表
          loadMaterials(pagination.current);
        }
      }
    } catch (error) {
      message.error('复制失败');
    }
  };

  // 移动素材
  const handleMoveMaterial = (material) => {
    setMovingMaterial(material);
    setMoveModalVisible(true);
  };

  // 确认移动素材
  const handleConfirmMove = async (targetGroupId) => {
    if (!movingMaterial) return;

    console.log('handleConfirmMove接收到的targetGroupId:', targetGroupId, '类型:', typeof targetGroupId);

    try {
      const params = {};
      // 直接使用targetGroupId，如果是null则表示移动到根目录（未分组）
      params.groupId = targetGroupId;

      console.log('移动素材请求参数:', params);
      const response = await api.put(`/materials/${movingMaterial.id}/move`, null, { params });
      if (response.data.success) {
        message.success('移动成功');
        setMoveModalVisible(false);
        setMovingMaterial(null);
        loadMaterials(pagination.current);
      } else {
        message.error(response.data.message || '移动失败');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('移动失败：' + error.message);
      }
    }
  };

  // 获取素材类型标签
  const getMaterialTypeTag = (type) => {
    const typeConfig = materialTypes.find(t => t.value === type);
    const colors = {
      TEXT: 'blue',
      IMAGE: 'green',
      VIDEO: 'purple',
      LINK: 'orange',
      MINIPROGRAM: 'red'
    };
    return (
      <Tag color={colors[type]}>
        {typeConfig ? typeConfig.label : type}
      </Tag>
    );
  };

  // 渲染网格视图素材卡片
  const renderGridMaterialCard = (material) => {
    const isSelected = selectedMaterials.includes(material.id);

    return (
      <Col span={6} key={material.id}>
        <div style={{
          height: '240px', // 固定高度容器，和MaterialSelector保持一致
          marginBottom: 32,
          display: 'flex',
          flexDirection: 'column',
          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: 'pointer'
        }}
          onClick={(e) => {
            // 如果点击的是操作按钮或checkbox区域，不触发预览
            if (e.target.closest('.material-actions') || e.target.closest('.material-checkbox')) {
              return;
            }
            // 点击预览
            // 这里可以使用 MaterialPreview 组件或自定义预览逻辑
          }}
        >
          <div style={{
            height: '180px', // 固定预览区域高度
            width: '100%',
            position: 'relative',
            cursor: 'pointer',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
            flexShrink: 0 // 防止被压缩
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              padding: '12px',
              boxSizing: 'border-box'
            }}>
              <MaterialPreview material={material} size="contain" />
            </div>
          </div>
          <Card
            // hoverable
            size="small"
            className="material-card"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              border: 'none',
              borderRadius: 0
            }}
            bodyStyle={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              padding: '8px 16px'
            }}
          >
            <div style={{
              position: 'relative',
              flex: 1, // 占满剩余空间
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end', // 内容靠底部对齐
              minHeight: 0 // 允许flex收缩
            }}>
              {/* 底部信息区域 - 紧凑布局 */}
              <div style={{ flexShrink: 0, border: 'none' }}>
                {/* 复选框和标题 */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                  <Checkbox
                    className="material-checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectedMaterials([...selectedMaterials, material.id]);
                      } else {
                        setSelectedMaterials(selectedMaterials.filter(id => id !== material.id));
                      }
                    }}
                    style={{ marginRight: '6px' }}
                  />
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    lineHeight: '16px'
                  }}>
                    {material.name}
                  </div>
                </div>

                {/* <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '4px',
                  minHeight: '18px'
                }}>
                  {getMaterialTypeTag(material.materialType)}
                  <span style={{ fontSize: '10px', color: '#999' }}>
                    {material.groupName || '未分组'}
                  </span>
                </div> */}

                {/* 操作按钮区域 - 紧凑布局 */}
                <div className="material-actions" style={{
                  display: 'flex',
                  gap: '8px', // 缩小按钮间距
                  justifyContent: 'start',
                  height: '18px', // 稍微减小按钮高度
                  alignItems: 'center'
                }}>
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditMaterial(material);
                    }}
                    style={{ padding: '0 10px', height: '18px', fontSize: '14px', minWidth: 'auto' }}
                  >
                    编辑
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyMaterial(material);
                    }}
                    style={{ padding: '0 10px', height: '18px', fontSize: '14px', minWidth: 'auto' }}
                  >
                    复制
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={(e) => {
                      e.stopPropagation();
                      Modal.confirm({
                        title: '确认删除',
                        content: `确定要删除素材"${material.name}"吗？`,
                        onOk: () => handleDeleteMaterial(material.id)
                      });
                    }}
                    style={{ padding: '0 10px', height: '18px', fontSize: '14px', minWidth: 'auto' }}
                  >
                    删除
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </Col>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: '素材名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 170,
      fixed: 'left',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: '类型',
      dataIndex: 'materialType',
      key: 'materialType',
      width: 80,
      render: (type) => getMaterialTypeTag(type)
    },
    {
      title: '所属分组',
      dataIndex: 'groupName',
      key: 'groupName',
      width: 100,
      ellipsis: true,
      render: (groupName) => (
        <span style={{ color: groupName ? '#666' : '#ccc' }}>
          {groupName || '未分组'}
        </span>
      )
    },
    {
      title: '预览',
      key: 'preview',
      width: 120,
      render: (_, record) => {
        return <MaterialPreview material={record} size={60} />;
      }
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (time) => time ? new Date(time).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            onClick={() => handleEditMaterial(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleCopyMaterial(record)}
          >
            复制
          </Button>
          <Button
            type="link"
            danger
            onClick={() => {
              Modal.confirm({
                title: '确认删除',
                content: `确定要删除素材"${record.name}"吗？`,
                onOk: () => handleDeleteMaterial(record.id)
              });
            }}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  // 如果没有tenantId，显示错误页面
  if (!tenantId) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <div style={{ marginBottom: '20px' }}>
          <FileTextOutlined style={{ fontSize: '64px', color: '#ccc' }} />
        </div>
        <h3>未找到租户信息</h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          请重新登录以获取租户信息
        </p>
        <Button type="primary" onClick={() => window.location.reload()}>
          刷新页面
        </Button>
      </div>
    );
  }

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      {/* 左侧分组树 */}
      <Sider width={200} theme="light" style={{ borderRight: '1px solid #f0f0f0', overflowX: 'hidden' }}>
        <div style={{ padding: '16px', paddingLeft: '0px' }}>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateGroup}
              block
            >
              新建分组
            </Button>
          </div>

          <Tree
            showIcon={false}
            showLine={false}
            treeData={groupTree}
            onSelect={handleGroupSelect}
            selectedKeys={selectedGroupId !== undefined && selectedGroupId !== null ? [selectedGroupId] : (selectedGroupId === null ? ['ungrouped_main'] : [])}
            className="materials-tree"
            style={{
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              padding: '8px 8px 8px 4px',
              overflowX: 'hidden'
            }}
          />
        </div>
      </Sider>

      {/* 右侧素材列表 */}
      <Content style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* 工具栏 */}
        <Card style={{ margin: 0, marginBottom: 0 }}>
          {/* 素材类型标签页 */}
          <div style={{ marginBottom: 16 }}>
            <Tabs
              activeKey={selectedMaterialType === null ? 'all' : selectedMaterialType}
              onChange={(key) => {
                const materialType = key === 'all' ? null : key;
                setSelectedMaterialType(materialType);
              }}
              type="line"
              size="middle"
              items={materialTypes.map(type => ({
                key: type.value === null ? 'all' : type.value,
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {/* {type.icon} */}
                    {type.label}
                  </span>
                )
              }))}
            />
          </div>

          {/* 第二行：搜索和操作按钮 */}
          <Row gutter={[16, 0]} align="middle">
            <Col span={12}>
              <Search
                placeholder="搜索素材名称、描述、文字内容..."
                allowClear
                enterButton
                onSearch={handleSearch}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                {/* 已选中素材提示 */}
                {selectedMaterials.length > 0 && (
                  <span style={{ color: '#1890ff', fontSize: '14px', marginRight: '8px' }}>
                    已选中 {selectedMaterials.length} 个素材
                  </span>
                )}

                {/* 视图切换按钮 */}
                <div style={{ display: 'flex', border: '1px solid #d9d9d9', borderRadius: '6px', overflow: 'hidden' }}>
                  <Button
                    type={viewMode === 'list' ? 'primary' : 'default'}
                    icon={<UnorderedListOutlined />}
                    size="small"
                    onClick={() => setViewMode('list')}
                    style={{
                      borderRadius: 0,
                      border: 'none',
                      boxShadow: 'none'
                    }}
                  >

                  </Button>
                  <Button
                    type={viewMode === 'grid' ? 'primary' : 'default'}
                    icon={<AppstoreOutlined />}
                    size="small"
                    onClick={() => setViewMode('grid')}
                    style={{
                      borderRadius: 0,
                      border: 'none',
                      boxShadow: 'none'
                    }}
                  >

                  </Button>
                </div>

                {selectedMaterialType ? (
                  <Button
                    type="primary"
                    onClick={() => handleCreateMaterial(selectedMaterialType)}
                  >
                    {`上传${(materialTypes.find(t => t.value === selectedMaterialType)?.label) || '素材'}`}
                  </Button>
                ) : (
                  <Dropdown
                    overlay={
                      <Menu>
                        {(() => {
                          const orderedTypes = ['TEXT', 'IMAGE', 'VIDEO', 'MINIPROGRAM', 'LINK'];
                          return orderedTypes.map(typeValue => {
                            const type = materialTypes.find(t => t.value === typeValue);
                            return type ? (
                              <Menu.Item
                                key={type.value}
                                onClick={() => handleCreateMaterial(type.value)}
                              >
                                {type.label}素材
                              </Menu.Item>
                            ) : null;
                          }).filter(Boolean);
                        })()}
                      </Menu>
                    }
                  >
                    <Button type="primary">上传素材</Button>
                  </Dropdown>
                )}

                <Button
                  disabled={selectedMaterials.length === 0}
                  onClick={handleBatchMove}
                >
                  批量移动
                </Button>

                <Popconfirm
                  title="确定要删除选中的素材吗？"
                  onConfirm={handleBatchDelete}
                  disabled={selectedMaterials.length === 0}
                >
                  <Button
                    disabled={selectedMaterials.length === 0}
                  >
                    批量删除
                  </Button>
                </Popconfirm>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 素材展示区域 */}
        <Card
          style={{ margin: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          bodyStyle={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          {viewMode === 'list' ? (
            // 列表视图
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Table
                dataSource={materials}
                columns={columns}
                loading={loading}
                rowKey="id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 个素材`,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
                rowSelection={{
                  selectedRowKeys: selectedMaterials,
                  onChange: setSelectedMaterials
                }}
                onChange={(paginationConfig) => {
                  loadMaterials(paginationConfig.current);
                }}
                scroll={{ y: 'calc(100vh - 280px)' }}
                size="middle"
              />
            </div>
          ) : (
            // 网格视图
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#fafafa', minHeight: 0 }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <div>加载中...</div>
                </div>
              ) : materials.length > 0 ? (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px', backgroundColor: 'white' }}>
                  <Row gutter={[16, 16]}>
                    {materials.map(renderGridMaterialCard)}
                  </Row>

                  {/* 分页 */}
                  {pagination.total > 0 && (
                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                      <Button
                        disabled={pagination.current === 1}
                        onClick={() => loadMaterials(pagination.current - 1)}
                      >
                        上一页
                      </Button>
                      <span style={{ margin: '0 16px' }}>
                        {pagination.current} / {Math.ceil(pagination.total / getPageSize())}
                      </span>
                      <Button
                        disabled={pagination.current >= Math.ceil(pagination.total / getPageSize())}
                        onClick={() => loadMaterials(pagination.current + 1)}
                      >
                        下一页
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                  暂无素材数据
                </div>
              )}
            </div>
          )}
        </Card>
      </Content>

      {/* 移动素材模态框 */}
      <Modal
        title={`移动素材: ${movingMaterial?.name || ''}`}
        open={moveModalVisible}
        onCancel={() => {
          setMoveModalVisible(false);
          setMovingMaterial(null);
        }}
        footer={null}
        width={400}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 16, color: '#666' }}>
            请选择要移动到的分组：
          </p>
          <Tree
            showIcon={false}
            treeData={(() => {
              // 从映射中获取所有真实分组（排除"全部"和"未分组"这两个虚拟节点）
              const realGroups = Array.from(groupsMap.values()).filter(g =>
                g.id !== null && g.id !== -1 && !g.parentId
              );
              // 移动目标：未分组 + 真实分组
              return [
                {
                  key: 'ungrouped',
                  title: '未分组'
                },
                ...convertToPureTreeData(realGroups)
              ];
            })()}
            onSelect={(selectedKeys) => {
              const selectedKey = selectedKeys[0];
              console.log('移动Tree选中的key:', selectedKey, '类型:', typeof selectedKey);

              // 和主要分组选择一样的key处理逻辑
              let actualGroupId;
              if (selectedKey === -1 || selectedKey === '-1') {
                actualGroupId = -1; // 全部（移动时不应该出现）
              } else if (selectedKey === 'ungrouped' || selectedKey === null || selectedKey === 'null' || typeof selectedKey === 'string' && selectedKey.includes('-')) {
                actualGroupId = null; // 未分组
              } else {
                // 具体分组ID
                actualGroupId = typeof selectedKey === 'string' ? parseInt(selectedKey) : selectedKey;
              }

              console.log('移动目标groupId:', actualGroupId);
              handleConfirmMove(actualGroupId);
            }}
            style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px' }}
          />
        </div>
      </Modal>

      {/* 批量移动素材模态框 */}
      <Modal
        title={`批量移动素材 (已选择 ${selectedMaterials.length} 个)`}
        open={batchMoveModalVisible}
        onCancel={() => {
          setBatchMoveModalVisible(false);
          setBatchMoveSelectedKeys([]); // 关闭时重置选中状态
        }}
        footer={null}
        width={400}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 16, color: '#666' }}>
            请选择要移动到的分组：
          </p>
          <Tree
            key={batchMoveTreeKey}
            showIcon={false}
            treeData={(() => {
              // 从映射中获取所有真实分组（排除"全部"和"未分组"这两个虚拟节点）
              const realGroups = Array.from(groupsMap.values()).filter(g =>
                g.id !== null && g.id !== -1 && !g.parentId
              );
              // 移动目标：未分组 + 真实分组
              return [
                {
                  key: null,
                  title: '未分组'
                },
                ...convertToPureTreeData(realGroups)
              ];
            })()}
            onSelect={(selectedKeys) => {
              const selectedKey = selectedKeys[0];
              console.log('批量移动Tree选中的key:', selectedKey, '类型:', typeof selectedKey);

              // 如果没有选中任何项（取消选中），不执行移动操作
              if (!selectedKeys.length) {
                console.log('取消选中，不执行移动操作');
                return;
              }

              // 和主要分组选择一样的key处理逻辑
              let actualGroupId;
              if (selectedKey === -1 || selectedKey === '-1') {
                actualGroupId = -1; // 全部（移动时不应该出现）
              } else if (selectedKey === null || selectedKey === 'null' || typeof selectedKey === 'string' && selectedKey.includes('-')) {
                actualGroupId = null; // 未分组
              } else {
                // 具体分组ID
                actualGroupId = typeof selectedKey === 'string' ? parseInt(selectedKey) : selectedKey;
              }

              console.log('批量移动目标groupId:', actualGroupId);
              handleConfirmBatchMove(actualGroupId);
            }}
            style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px' }}
          />
        </div>
      </Modal>

      {/* 分组编辑模态框 */}
      <MaterialGroupModal
        visible={groupModalVisible}
        editingGroup={editingGroup}
        allGroups={Array.from(groupsMap.values()).filter(g =>
          g.id !== null && g.id !== -1 // 过滤掉虚拟节点（"全部"和"未分组"）
        )}
        tenantId={tenantId}
        onCancel={() => setGroupModalVisible(false)}
        onSuccess={() => {
          setGroupModalVisible(false);
          loadGroupTree();
        }}
      />

      {/* 素材编辑模态框 - 暂时注释，待创建组件 */}
      {false && (
        <div>MaterialEditModal组件待创建</div>
      )}

      <style jsx global>{`
        .ant-card-actions {
          border-top: none;
          padding: 8px 0;
        }
        
        /* Tree 节点内按钮区域可点击且不被遮挡（仅作用于本页 Tree） */
        .materials-tree .materials-tree-actions,
        .materials-tree .materials-tree-actions *,
        .materials-tree .materials-tree-actions .ant-dropdown,
        .materials-tree .materials-tree-actions .ant-dropdown-trigger {
          pointer-events: auto !important;
          position: relative !important;
          z-index: 3 !important;
          cursor: pointer;
        }
        .materials-tree .ant-tree-node-content-wrapper {
          position: relative;
          overflow: visible;
        }
        .materials-tree .ant-tree-title {
          overflow: visible;
        }
        
        /* 自定义Tree选中状态的背景色 - 更精确的选择器 */
        body .ant-tree .ant-tree-node-selected > .ant-tree-node-content-wrapper,
        body .ant-tree .ant-tree-node-selected .ant-tree-node-content-wrapper {
          background-color: #52c41a !important;
          color: white !important;
        }
        
        body .ant-tree .ant-tree-node-selected > .ant-tree-node-content-wrapper:hover,
        body .ant-tree .ant-tree-node-selected .ant-tree-node-content-wrapper:hover {
          background-color: #389e0d !important;
          color: white !important;
        }
        
        /* 确保选中状态优先级最高 */
        html body .ant-tree .ant-tree-node-selected > .ant-tree-node-content-wrapper,
        html body .ant-tree .ant-tree-node-selected .ant-tree-node-content-wrapper {
          background-color: #52c41a !important;
          color: white !important;
        }
        
        html body .ant-tree .ant-tree-node-selected > .ant-tree-node-content-wrapper:hover,
        html body .ant-tree .ant-tree-node-selected .ant-tree-node-content-wrapper:hover {
          background-color: #389e0d !important;
          color: white !important;
        }
        
        /* 最高优先级覆盖 - 针对所有可能的Tree节点结构 */
        .ant-tree-node-selected,
        .ant-tree-node-selected > *,
        .ant-tree-node-selected .ant-tree-title,
        .ant-tree-node-selected .ant-tree-node-content-wrapper {
          background-color: #52c41a !important;
          background: #52c41a !important;
          color: white !important;
        }
        
        .ant-tree-node-selected:hover,
        .ant-tree-node-selected > *:hover,
        .ant-tree-node-selected .ant-tree-title:hover,
        .ant-tree-node-selected .ant-tree-node-content-wrapper:hover {
          background-color: #389e0d !important;
          background: #389e0d !important;
          color: white !important;
        }

        /* 分组树布局：让内容区占满而不超出，名称可省略，按钮可见 */
        .materials-tree .ant-tree-treenode {
          display: flex;
          align-items: center;
          width: 100%;
        }
        .materials-tree .ant-tree-node-content-wrapper {
          flex: 1;
          min-width: 0;
        }

        /* 缩小整体左侧缩进与层级缩进宽度 */
        .materials-tree .ant-tree-switcher {
          width: 12px !important;
          min-width: 12px !important;
        }
        .materials-tree .ant-tree-indent-unit {
          width: 12px !important;
        }
        .materials-tree .ant-tree-node-content-wrapper {
          padding-left: 2px;
        }
        
        /* 确保网格卡片固定高度对齐 - 现在只是底部信息区域 */
        .material-card {
          height: 40px !important;
        }
        
        /* 确保预览区域完整显示图片和视频 - 更新为新的DOM结构 */
        div[style*="height: 180px"] {
          background-color: #f8f9fa !important;
          overflow: hidden !important;
          height: 180px !important;
          max-height: 180px !important;
          flex-shrink: 0 !important;
        }
        
        div[style*="height: 180px"] > div {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100% !important;
          height: 180px !important;
        }
        
        /* 强制所有图片和视频在容器内完整显示，使用contain模式 */
        div[style*="height: 180px"] img,
        div[style*="height: 180px"] video {
          max-width: 100% !important;
          max-height: 100% !important;
          width: auto !important;
          height: auto !important;  
          object-fit: contain !important;
          object-position: center !important;
          display: block !important;
        }
        
        div[style*="height: 180px"] .ant-image {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          max-width: 100% !important;
          max-height: 100% !important;
          width: 100% !important;
          height: 100% !important;
        }
        
        div[style*="height: 180px"] .ant-image-img {
          max-width: 100% !important;
          max-height: 100% !important;
          width: auto !important;
          height: auto !important;
          object-fit: contain !important;
          object-position: center !important;
        }
        
        /* 确保所有嵌套容器都遵循contain规则 */
        div[style*="height: 180px"] > div > * {
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: contain !important;
        }
        
        /* 确保所有预览内容都被约束在容器内并居中 */
        div[style*="height: 180px"] > div > div {
          max-width: calc(100% - 24px) !important;
          max-height: calc(100% - 24px) !important;
          overflow: hidden !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        
        /* MaterialPreview 组件内的所有元素都要受容器限制 */
        div[style*="height: 180px"] * {
          box-sizing: border-box !important;
        }
        
        /* 确保MaterialPreview组件内的内容完整显示 */
        div[style*="height: 180px"] > div > div {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        
        /* 特殊处理预览组件确保内容不被裁剪 */
        div[style*="height: 180px"] img[src],
        div[style*="height: 180px"] video[src] {
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: contain !important;
        }
        
        /* 文本类型素材样式 */
        div[style*="height: 180px"] .material-text-preview {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          text-align: center;
          max-width: 100%;
          max-height: 100%;
          overflow: hidden;
          background-color: white;
        }
        
        /* 视频播放按钮样式 */
        div[style*="height: 180px"] .video-play-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 2;
        }
      `}</style>
    </Layout>
  );
};

export default Materials; 
